// src/pages/auth/RegisterPage.js
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthForm from '../../components/auth/AuthForm';
import AuthTabs from '../../components/auth/AuthTabs';
import AuthService from '../../services/AuthService';
import '../../components/auth/styles/auth.css';
import '../../components/auth/styles/client.css';
import '../../components/auth/styles/driver.css';

// Lista de tipos de veículo (mesma do app)
const VEHICLE_TYPES = [
  { value: 'moto', label: '🏍️ Moto' },
  { value: 'txopela', label: '🚐 Txopela' },
  { value: 'van', label: '🚌 Van' },
  { value: 'ligeira', label: '🚗 Viatura Ligeira' },
  { value: 'pickup', label: '🛻 Pickup' },
  { value: 'camioneta', label: '🚙 Camioneta' },
  { value: 'camiao_pequeno', label: '🚚 Camião Pequeno' },
  { value: 'camiao_medio', label: '🚛 Camião Médio' },
  { value: 'camiao_grande', label: '🚛 Camião Grande' },
];

// Prefixos válidos para telefone Moçambique
const VALID_PREFIXES = ['82', '83', '84', '85', '86', '87'];

const RegisterPage = ({
  isModal = false,
  onClose = () => {},
  onRegisterSuccess = () => {},
  userType = 'client',
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Estado do formulário
  const [formData, setFormData] = useState({
    user_type: userType === 'driver' ? 'driver' : 'client',
    nome: '',
    phone: location.state?.phone || '',
    email: '',
    password: '',
    marca_veiculo: '',
    modelo_veiculo: '',
    tipo_veiculo: '',
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Atualiza user_type se vier via location state
  useEffect(() => {
    if (location.state?.user_type) {
      setFormData((prev) => ({ ...prev, user_type: location.state.user_type }));
    }
  }, [location.state]);

  // Normaliza telefone para +258XXXXXXXXX
  const normalizePhone = (raw) => {
    const digits = raw.replace(/\D/g, '');
    if (digits.startsWith('258')) {
      return '+' + digits;
    }
    const local = digits.slice(-9);
    if (local.length === 9) {
      return '+258' + local;
    }
    return raw;
  };

  // Validação de telefone
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    const local = cleaned.startsWith('258') ? cleaned.slice(3) : cleaned;
    if (local.length !== 9) return 'Número deve ter 9 dígitos após +258';
    const prefix = local.substring(0, 2);
    if (!VALID_PREFIXES.includes(prefix)) {
      return 'Número inválido. Use prefixos 82–87';
    }
    return null;
  };

  // Validação de email
  const validateEmail = (email) => {
    if (!email) return true;
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    const cleaned = raw.slice(-9);
    setFormData((prev) => ({ ...prev, phone: cleaned }));
    setErrors((prev) => ({ ...prev, phone: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    // --- Validações ---
    if (!formData.nome.trim()) {
      setErrors({ nome: 'Nome completo é obrigatório.' });
      setLoading(false);
      return;
    }
    if (formData.nome.trim().length < 3) {
      setErrors({ nome: 'Nome deve ter pelo menos 3 caracteres.' });
      setLoading(false);
      return;
    }

    const phoneError = validatePhone(formData.phone);
    if (phoneError) {
      setErrors({ phone: phoneError });
      setLoading(false);
      return;
    }

    if (formData.email && !validateEmail(formData.email)) {
      setErrors({ email: 'E-mail inválido.' });
      setLoading(false);
      return;
    }

    if (!formData.password) {
      setErrors({ password: 'Senha é obrigatória.' });
      setLoading(false);
      return;
    }
    if (formData.password.length < 6) {
      setErrors({ password: 'Senha deve ter pelo menos 6 caracteres.' });
      setLoading(false);
      return;
    }

    if (!acceptedTerms) {
      setErrors({ terms: 'Você precisa aceitar os Termos de Uso.' });
      setLoading(false);
      return;
    }

    if (formData.user_type === 'driver') {
      if (!formData.marca_veiculo.trim()) {
        setErrors({ marca_veiculo: 'Marca do veículo é obrigatória.' });
        setLoading(false);
        return;
      }
      if (!formData.modelo_veiculo.trim()) {
        setErrors({ modelo_veiculo: 'Modelo do veículo é obrigatório.' });
        setLoading(false);
        return;
      }
      if (!formData.tipo_veiculo) {
        setErrors({ tipo_veiculo: 'Tipo de veículo é obrigatório.' });
        setLoading(false);
        return;
      }
    }

    // --- Montagem do payload ---
    const fullPhone = normalizePhone(formData.phone);
    let payload = {};

    if (formData.user_type === 'client') {
      payload = {
        phone: fullPhone,
        password: formData.password,
        name: formData.nome.trim(),
        email: formData.email.trim() || null,
      };
    } else {
      payload = {
        name: formData.nome.trim(),
        phone: fullPhone,
        email: formData.email.trim() || null,
        password: formData.password,
        marca_veiculo: formData.marca_veiculo.trim(),
        modelo_veiculo: formData.modelo_veiculo.trim(),
        tipo_veiculo: formData.tipo_veiculo,
      };
    }

    console.log('[RegisterPage] Payload enviado:', payload);

    try {
      // Chama o AuthService.register (já salva token e user no localStorage)
      await AuthService.register(payload, formData.user_type);

      // ✅ REMOVIDO: loadUserProfile() - não é necessário, o registro já retornou o usuário completo

      setSuccessMessage('Cadastro realizado com sucesso!');
      onRegisterSuccess();

      // Redireciona para a tela de sucesso após curto delay
      setTimeout(() => {
        if (isModal) {
          onClose();
        } else {
          navigate('/register-success', {
            state: { userType: formData.user_type }
          });
        }
      }, 1000);
    } catch (err) {
      console.error('[RegisterPage] Erro no cadastro:', err);
      // Tratamento de erros do backend
      if (err.response && err.response.data) {
        const backendErrors = err.response.data.errors || err.response.data.message;
        if (typeof backendErrors === 'string') {
          setErrors({ non_field_errors: backendErrors });
        } else if (typeof backendErrors === 'object') {
          const mapped = {};
          if (backendErrors.name) mapped.nome = backendErrors.name[0] || backendErrors.name;
          if (backendErrors.phone) mapped.phone = backendErrors.phone[0] || backendErrors.phone;
          if (backendErrors.email) mapped.email = backendErrors.email[0] || backendErrors.email;
          if (backendErrors.password) mapped.password = backendErrors.password[0] || backendErrors.password;
          if (backendErrors.marca_veiculo) mapped.marca_veiculo = backendErrors.marca_veiculo[0] || backendErrors.marca_veiculo;
          if (backendErrors.modelo_veiculo) mapped.modelo_veiculo = backendErrors.modelo_veiculo[0] || backendErrors.modelo_veiculo;
          if (backendErrors.tipo_veiculo) mapped.tipo_veiculo = backendErrors.tipo_veiculo[0] || backendErrors.tipo_veiculo;
          if (backendErrors.non_field_errors) mapped.non_field_errors = backendErrors.non_field_errors;
          setErrors(mapped);
        } else {
          setErrors({ non_field_errors: 'Erro no cadastro. Tente novamente.' });
        }
      } else {
        setErrors({ non_field_errors: err.message || 'Erro inesperado.' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Renderiza o formulário
  const renderFormFields = () => (
    <>
      <AuthTabs
        userType={formData.user_type}
        setUserType={(type) =>
          setFormData((prev) => ({
            ...prev,
            user_type: type === 'driver' ? 'driver' : 'client',
            marca_veiculo: '',
            modelo_veiculo: '',
            tipo_veiculo: '',
          }))
        }
      />

      <div className="form-group">
        <label htmlFor="nome">Nome completo *</label>
        <input
          id="nome"
          name="nome"
          type="text"
          placeholder="Ex: João Macamo"
          value={formData.nome}
          onChange={handleChange}
          disabled={loading}
          className={errors.nome ? 'input-error' : ''}
        />
        {errors.nome && <div className="error-message">{errors.nome}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="phone">Telefone *</label>
        <div className="phone-input-wrapper">
          <span className="phone-prefix">+258</span>
          <input
            id="phone"
            name="phone"
            type="text"
            placeholder="821234567"
            value={formData.phone}
            onChange={handlePhoneChange}
            disabled={loading}
            className={errors.phone ? 'input-error' : ''}
            maxLength="9"
          />
        </div>
        {errors.phone && <div className="error-message">{errors.phone}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="email">
          E-mail <span className="optional">(opcional)</span>
        </label>
        <input
          id="email"
          name="email"
          type="email"
          placeholder="exemplo@email.com"
          value={formData.email}
          onChange={handleChange}
          disabled={loading}
          className={errors.email ? 'input-error' : ''}
        />
        {errors.email && <div className="error-message">{errors.email}</div>}
      </div>

      <div className="form-group">
        <label htmlFor="password">Senha *</label>
        <div className="password-input-wrapper">
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Mínimo 6 caracteres"
            value={formData.password}
            onChange={handleChange}
            disabled={loading}
            className={errors.password ? 'input-error' : ''}
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex="-1"
          >
            {showPassword ? '🙈' : '👁️'}
          </button>
        </div>
        {errors.password && <div className="error-message">{errors.password}</div>}
      </div>

      {formData.user_type === 'driver' && (
        <div className="driver-fields">
          <h4 className="section-title">Informações do Veículo</h4>

          <div className="form-group">
            <label htmlFor="marca_veiculo">Marca do veículo *</label>
            <input
              id="marca_veiculo"
              name="marca_veiculo"
              type="text"
              placeholder="Ex: Toyota, Honda"
              value={formData.marca_veiculo}
              onChange={handleChange}
              disabled={loading}
              className={errors.marca_veiculo ? 'input-error' : ''}
            />
            {errors.marca_veiculo && <div className="error-message">{errors.marca_veiculo}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="modelo_veiculo">Modelo do veículo *</label>
            <input
              id="modelo_veiculo"
              name="modelo_veiculo"
              type="text"
              placeholder="Ex: Hilux, Corolla"
              value={formData.modelo_veiculo}
              onChange={handleChange}
              disabled={loading}
              className={errors.modelo_veiculo ? 'input-error' : ''}
            />
            {errors.modelo_veiculo && <div className="error-message">{errors.modelo_veiculo}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="tipo_veiculo">Tipo de veículo *</label>
            <select
              id="tipo_veiculo"
              name="tipo_veiculo"
              value={formData.tipo_veiculo}
              onChange={handleChange}
              disabled={loading}
              className={errors.tipo_veiculo ? 'input-error' : ''}
            >
              <option value="">Selecione o tipo</option>
              {VEHICLE_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            {errors.tipo_veiculo && <div className="error-message">{errors.tipo_veiculo}</div>}
          </div>
        </div>
      )}

      <div className="form-group terms-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => {
              setAcceptedTerms(e.target.checked);
              if (e.target.checked) {
                setErrors((prev) => ({ ...prev, terms: '' }));
              }
            }}
            disabled={loading}
          />
          <span>
            Li e aceito os{' '}
            <a href="/termos" target="_blank" rel="noopener noreferrer">
              Termos de Uso
            </a>{' '}
            e{' '}
            <a href="/privacidade" target="_blank" rel="noopener noreferrer">
              Política de Privacidade
            </a>
          </span>
        </label>
        {errors.terms && <div className="error-message">{errors.terms}</div>}
      </div>

      {errors.non_field_errors && (
        <div className="error-message non-field">{errors.non_field_errors}</div>
      )}

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      <button type="submit" disabled={loading} className="btn-submit">
        {loading ? 'Cadastrando...' : 'Cadastrar'}
      </button>
    </>
  );

  return (
    <div className="page-container">
      <div className="auth-container">
        {!isModal && (
          <button className="back-button" onClick={() => navigate(-1)}>
            ← Voltar
          </button>
        )}

        <AuthForm
          title={`Cadastro (${formData.user_type === 'driver' ? 'Motorista' : 'Cliente'})`}
          onSubmit={handleSubmit}
        >
          {renderFormFields()}
        </AuthForm>
      </div>
    </div>
  );
};

RegisterPage.propTypes = {
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  onRegisterSuccess: PropTypes.func,
  userType: PropTypes.oneOf(['client', 'driver']),
};

export default RegisterPage;